import { Steps } from 'antd';
import { useMemo, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetRouterSpecsQuery } from '../../../api/queries/getRouterSpecs';
import LoadingElement from '../../../components/LoadingElement';
import NotFoundResult from '../../../components/results/NotFoundResult';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import EndpointClassConfiguration from './EndpointClassConfiguration';
import EndpointClassSelection from './EndpointClassSelection';
import { type EndpointClass } from './getAvailableEndpoints';

type State = { step: 'selection' } | { step: 'settings'; selectedClass: EndpointClass };

type Action = { type: 'goToSelection' } | { type: 'goToSettings'; selectedClass: EndpointClass };

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case 'goToSelection':
            return { step: 'selection' };

        case 'goToSettings':
            return { step: 'settings', selectedClass: action.selectedClass };

        default:
            return state;
    }
};

const AddEndpointPage = () => {
    const { routerId } = useParams<{ routerId: string }>();
    const { t } = useTranslation(['routerDetails']);
    const navigate = useNavigate();
    const [state, dispatch] = useReducer(reducer, { step: 'selection' });

    const actions = useMemo(
        () => ({
            goToSelection: () => dispatch({ type: 'goToSelection' }),
            goToSettings: (endpointClass: EndpointClass) =>
                dispatch({ type: 'goToSettings', selectedClass: endpointClass }),
        }),
        [dispatch]
    );

    const { data, loading } = useGetRouterSpecsQuery({
        fetchPolicy: 'cache-and-network',
        variables: { routerId },
    });

    const router = data?.router;

    if (!router && loading) {
        return <LoadingElement />;
    }

    if (!router) {
        return <NotFoundResult />;
    }

    const content = (
        <div style={{ maxWidth: '960px', margin: 'auto' }}>
            <Steps current={state.step === 'selection' ? 0 : 1} type="default">
                <Steps.Step
                    status={state.step === 'selection' ? 'process' : 'finish'}
                    title={t('routerDetails:addEndpoint.classSelection.title')}
                />
                <Steps.Step
                    status={state.step === 'settings' ? 'process' : 'wait'}
                    title={t('routerDetails:addEndpoint.configuration.title')}
                />
            </Steps>
        </div>
    );

    const body =
        state.step === 'selection' ? (
            <EndpointClassSelection onSelect={actions.goToSettings} router={router} />
        ) : (
            <EndpointClassConfiguration
                endpointClass={state.selectedClass}
                goBackToSelection={actions.goToSelection}
                router={router}
            />
        );

    return (
        <ConsolePageWithHeader
            content={content}
            onBack={() => navigate(`/admin/system/routers/${routerId}`)}
            subTitle={t('routerDetails:addEndpoint.subTitle')}
            title={t('routerDetails:addEndpoint.title')}
        >
            {body}
        </ConsolePageWithHeader>
    );
};

export default AddEndpointPage;
